// pp-calc-caja-o-bolsa.js — ¿Caja o Bolsa? Elige el Empaque Correcto
// (/pages/caja-o-bolsa)
//
// Wizard editorial para quien empieza a vender y no sabe ni por dónde. La
// matriz de decisión es la REGLA PRÁCTICA DE PARA PAQUETES (experiencia de
// almacén, no una norma):
//
//   · Se rompe o se deforma → CAJA + relleno siempre. Si además es un
//     accesorio chico (menos de 1 kg, pieza única), el sobre con burbuja es
//     el camino medio para ahorrar.
//   · Líquido o con riesgo de derrame → CAJA siempre, nunca bolsa sola:
//     doble empaque (envase cerrado + bolsa interior sellada + caja).
//   · Extremadamente frágil (vidrio delgado, arte, instrumentos) → CAJA con
//     relleno generoso siempre; si además pesa, doble corrugado.
//   · Se arruga pero no se rompe → bolsa solo si pesa menos de 1 kg y viaja
//     una sola pieza; caja en los demás casos.
//   · Aguanta apretones → BOLSA hasta 5 kg (y adiós peso volumétrico);
//     caja arriba de 5 kg o con varias piezas medianas por pedido.
//   · Más de 20 kg → caja SIEMPRE (la bolsa se rasga con su propio peso),
//     de doble corrugado, y aviso del límite típico de paquetería (~25 kg).
//   · Entrega local propia y cliente que recoge → relajan un escalón de
//     peso: ese viaje maltrata menos que la banda de la paquetería.
//   · Mensajería por app (Uber Flash, DiDi, Rappi) → trato intermedio:
//     relaja el escalón solo para lo que aguanta apretones; lo que se
//     arruga no se salva porque el paquete viaja suelto, sin apilado
//     controlado.
//   · Fulfillment de marketplace (FBA / Full) → caja casi siempre y
//     recordatorio de que el marketplace tiene requisitos propios.
//   · Lo frágil (rompe / derrame / extremo) NUNCA se relaja por el envío:
//     el riesgo vive en el producto, no solo en el viaje.
//
// Lógica pura probada en tests/calc-caja-o-bolsa.test.mjs (matriz original,
// intacta) y tests/calc-caja-o-bolsa-opciones.test.mjs (barrido completo de
// las opciones ampliadas).
(function (root) {
  'use strict';

  var FRAGILIDADES = ['aguanta', 'arruga', 'rompe', 'derrame', 'extremo'];
  var ENVIOS = ['paqueteria', 'local', 'app', 'recoge', 'fulfillment'];

  // Pesos: cada value mapea a un escalón lógico (0 = ligero <1 kg,
  // 1 = mediano 1–5 kg, 2 = pesado >5 kg). Los tres values viejos del
  // wizard original siguen vivos con su comportamiento EXACTO; los ocho
  // nuevos dan granularidad sobre la misma matriz.
  var PESO_IDX = {
    // values viejos (compatibilidad: mismo veredicto que siempre)
    'menos1': 0, '1a5': 1, 'mas5': 2,
    // values nuevos granulares (los del select actual)
    'menos250': 0, '250a500': 0, '500a1000': 0,
    '1a2': 1, '2a5': 1,
    '5a10': 2, '10a20': 2, 'mas20': 2
  };

  // Lista fija de compras: las únicas URLs que el resultado puede pintar.
  // Handles verificados vivos en parapaquetes.com (products.json 200).
  var COMPRAS = {
    'bolsas-para-envios': {
      url: '/collections/bolsas-para-envios',
      titulo: 'Bolsas para Envíos',
      nota: 'Ligeras, resistentes y casi sin volumen: pagas el peso real'
    },
    'cajas-de-carton-regulares': {
      url: '/collections/cajas-de-carton-regulares',
      titulo: 'Cajas de Cartón Regulares',
      nota: 'Paredes rígidas que reciben el golpe por tu producto'
    },
    'material-de-proteccion-y-relleno': {
      url: '/collections/material-de-proteccion-y-relleno',
      titulo: 'Material de Protección y Relleno',
      nota: 'Burbuja y relleno: el producto no debe viajar bailando'
    },
    'sobres-con-burbuja': {
      url: '/collections/sobres-con-burbuja',
      titulo: 'Sobres con Burbuja',
      nota: 'El camino medio: burbuja integrada a precio de sobre'
    }
  };

  // El veredicto base (condiciones de paquetería) para una fragilidad,
  // un índice de peso (0 = <1 kg, 1 = 1–5 kg, 2 = >5 kg) y multi-pieza.
  function base(frag, pesoIdx, multi) {
    if (frag === 'rompe') return 'caja';
    if (frag === 'arruga') return (pesoIdx === 0 && !multi) ? 'bolsa' : 'caja';
    // aguanta: bolsa hasta 5 kg; caja arriba de 5 kg o multi-pieza mediana.
    return (pesoIdx === 2 || (pesoIdx === 1 && multi)) ? 'caja' : 'bolsa';
  }

  // El porqué de cada veredicto, en lenguaje de quien empieza. Vive en la
  // parte pura para que los tests verifiquen que toda razón tiene texto.
  var RAZONES = {
    fragil: 'Lo que se rompe o se deforma necesita paredes rígidas y burbuja que absorba el golpe — una bolsa no lo va a salvar.',
    arruga: 'Bajo presión se arruga: con más peso encima o varias piezas necesita la estructura de una caja para llegar presentable.',
    peso: 'Arriba de 5 kg (o con varias piezas medianas) la bolsa se rasga en el manejo y el contenido se golpea entre sí.',
    maltrato: 'La paquetería avienta, apila y voltea los paquetes en cada banda — el empaque carga el maltrato por tu producto.',
    aguanta: 'Tu producto tolera apretones: no necesita pagar la rigidez de una caja para llegar bien.',
    ligero: 'Ligero y en una sola pieza: la bolsa lo envuelve sin arrugarlo más de lo que una caja holgada lo dejaría moverse.',
    local: 'Tu entrega propia maltrata mucho menos que la banda de una paquetería, así que la bolsa alcanza un escalón más de peso.',
    volumetrico: 'La bolsa se pega al producto y casi no suma volumen: el peso volumétrico deja de castigar tu tarifa de envío.',
    derrame: 'Un líquido o algo que se puede derramar nunca viaja en bolsa sola: si el envase se abre en el camino, nada contiene la fuga. Va en caja, con el envase bien cerrado dentro de una bolsa interior sellada.',
    extremo: 'Lo extremadamente frágil — vidrio delgado, arte, instrumentos — va siempre en caja con relleno generoso por los seis lados: el producto no debe tocar las paredes ni moverse un centímetro.',
    appTrato: 'La mensajería por app maltrata menos que una paquetería, pero tu paquete viaja suelto en una cajuela o mochila, sin apilado controlado — puede acabar debajo de otra carga.',
    appRelaja: 'La mensajería por app no avienta ni apila como la banda de una paquetería: para un producto que aguanta apretones, la bolsa alcanza un escalón más de peso.',
    appBolsa: 'En la app tu paquete viaja suelto en cajuela o mochila, y la bolsa compacta viaja mejor: envuelve tu producto sin pagar empaque de más.',
    recogeTrato: 'Aunque la entrega sea en mano, este caso pide caja: el riesgo vive en el producto mismo — o en las piezas golpeándose entre sí — no solo en el viaje.',
    recogeRelaja: 'Con paquetería este peso ya pediría caja, pero al recogerlo el cliente en persona el paquete no vive bandas ni torres de paquetes — la bolsa sigue alcanzando.',
    recogeBolsa: 'Entrega en mano, el viaje más amable que existe: el empaque solo necesita presentar bien tu producto, y la bolsa lo hace sin costo de más.',
    fulfillment: 'El fulfillment del marketplace (Amazon FBA, Mercado Envíos Full) mueve tu producto por varias bodegas y bandas, y exige empaque rígido casi siempre.',
    fulfillmentNota: 'Ojo: cada marketplace tiene requisitos propios de empaque (medidas, resistencia, etiquetado) — revísalos antes de mandar inventario.',
    limite25: 'Con más de 20 kg estás rozando el límite que maneja la mayoría de las paqueterías (alrededor de 25 kg por paquete): confirma el máximo con la tuya o divide el pedido en dos cajas.'
  };

  // Notas extra del veredicto (párrafos que se suman debajo de las razones).
  var NOTAS = {
    sobreCaminoMedio: 'Camino medio para ahorrar: un accesorio chico y frágil también viaja seguro en un sobre con burbuja — protección integrada sin pagar caja ni relleno.',
    dobleEmpaque: 'Regla de oro para líquidos: doble empaque. El envase bien cerrado, dentro de una bolsa interior sellada, y todo dentro de la caja con relleno — si algo se abre, el derrame se queda en la bolsa y no arruina etiquetas ni paquetes vecinos.',
    dobleCorrugado: 'Para este caso te conviene caja de doble corrugado: pared doble que no se vence con el peso ni se rinde en las esquinas.'
  };

  // Entradas: fragilidad ('aguanta'|'arruga'|'rompe'|'derrame'|'extremo'),
  // peso (cualquier llave de PESO_IDX), multiPieza (checkbox: true|'on'),
  // envio ('paqueteria'|'local'|'app'|'recoge'|'fulfillment').
  function decidir(e) {
    if (!e) return null;
    var frag = e.fragilidad, peso = e.peso, envio = e.envio;
    var multi = e.multiPieza === true || e.multiPieza === 'on';
    if (FRAGILIDADES.indexOf(frag) === -1) return null;
    if (!Object.prototype.hasOwnProperty.call(PESO_IDX, peso)) return null;
    if (ENVIOS.indexOf(envio) === -1) return null;

    var pesoIdx = PESO_IDX[peso];
    var muyPesado = peso === 'mas20';
    // Fragilidades que jamás se relajan por el envío: el riesgo vive en el
    // producto (se rompe, se derrama) — no solo en el viaje.
    var fragilTotal = frag === 'rompe' || frag === 'derrame' || frag === 'extremo';

    // Veredicto base (condiciones de paquetería).
    var veredicto;
    if (frag === 'derrame' || frag === 'extremo') veredicto = 'caja';
    else veredicto = base(frag, pesoIdx, multi);

    // Fulfillment de marketplace: exige caja casi siempre; la única
    // excepción es lo que aguanta apretones, ligero y pieza única.
    var forzadoPorMarketplace = false;
    if (envio === 'fulfillment' && veredicto === 'bolsa' &&
        !(frag === 'aguanta' && pesoIdx === 0 && !multi)) {
      veredicto = 'caja';
      forzadoPorMarketplace = true;
    }

    // El trato del envío relaja UN escalón de peso — nunca lo frágil y
    // nunca arriba de 20 kg (la bolsa se rasga con su propio peso):
    //   · local y recoge relajan siempre;
    //   · la app solo para lo que aguanta apretones (el paquete viaja
    //     suelto, sin apilado controlado — lo que se arruga no se salva).
    var relajadoPorLocal = false, relajadoPorRecoge = false, relajadoPorApp = false;
    var envioRelaja = envio === 'local' || envio === 'recoge' ||
      (envio === 'app' && frag === 'aguanta');
    if (envioRelaja && veredicto === 'caja' && !fragilTotal && !muyPesado && pesoIdx > 0) {
      if (base(frag, pesoIdx - 1, multi) === 'bolsa') {
        veredicto = 'bolsa';
        if (envio === 'local') relajadoPorLocal = true;
        else if (envio === 'recoge') relajadoPorRecoge = true;
        else relajadoPorApp = true;
      }
    }

    // Accesorio chico y frágil: la caja es lo seguro, pero el sobre con
    // burbuja es el punto medio honesto para ahorrar (no aplica en
    // fulfillment: el marketplace quiere caja).
    var sobreCaminoMedio = frag === 'rompe' && pesoIdx === 0 && !multi &&
      envio !== 'fulfillment';

    // Notas de empaque reforzado.
    var dobleEmpaque = frag === 'derrame';
    var dobleCorrugado = muyPesado || (frag === 'extremo' && pesoIdx === 2);
    var limitePaqueteria = muyPesado &&
      (envio === 'paqueteria' || envio === 'app' || envio === 'fulfillment');

    var razones;
    if (veredicto === 'caja') {
      if (forzadoPorMarketplace) {
        razones = ['fulfillment', 'fulfillmentNota'];
      } else {
        if (frag === 'derrame') razones = ['derrame'];
        else if (frag === 'extremo') razones = ['extremo'];
        else if (frag === 'rompe') razones = ['fragil'];
        else if (frag === 'arruga') razones = ['arruga'];
        else razones = ['peso'];
        if (envio === 'app') razones.push('appTrato');
        else if (envio === 'recoge') razones.push('recogeTrato');
        else if (envio === 'fulfillment') razones.push('fulfillment');
        else razones.push('maltrato');
      }
    } else {
      if (relajadoPorLocal) razones = ['local', 'volumetrico'];
      else if (relajadoPorRecoge) razones = ['recogeRelaja', 'recogeBolsa'];
      else if (relajadoPorApp) razones = ['appRelaja', 'appBolsa'];
      else {
        razones = [frag === 'arruga' ? 'ligero' : 'aguanta'];
        if (envio === 'recoge') razones.push('recogeBolsa');
        else if (envio === 'app') razones.push('appBolsa');
        else razones.push('volumetrico');
      }
      if (envio === 'fulfillment') razones.push('fulfillmentNota');
    }
    if (limitePaqueteria) razones.push('limite25');

    var compras = veredicto === 'bolsa'
      ? ['bolsas-para-envios', 'sobres-con-burbuja']
      : ['cajas-de-carton-regulares', 'material-de-proteccion-y-relleno'];
    if (veredicto === 'caja' && sobreCaminoMedio) compras.push('sobres-con-burbuja');
    // Doble empaque: la bolsa interior sellada también es compra concreta.
    if (dobleEmpaque) compras.push('bolsas-para-envios');

    return {
      veredicto: veredicto,
      relajadoPorLocal: relajadoPorLocal,
      relajadoPorRecoge: relajadoPorRecoge,
      relajadoPorApp: relajadoPorApp,
      forzadoPorMarketplace: forzadoPorMarketplace,
      sobreCaminoMedio: sobreCaminoMedio,
      dobleEmpaque: dobleEmpaque,
      dobleCorrugado: dobleCorrugado,
      limitePaqueteria: limitePaqueteria,
      razones: razones,
      compras: compras
    };
  }

  root.PPCajaOBolsa = {
    COMPRAS: COMPRAS,
    RAZONES: RAZONES,
    NOTAS: NOTAS,
    decidir: decidir
  };
})(typeof window !== 'undefined' ? window : globalThis);

// ── Wiring de la página (no corre en tests de node) ────────────────────────
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  function listo(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  listo(function () {
    var raiz = document.querySelector('[data-pp-calc-caja-o-bolsa]');
    if (!raiz) return;
    var C = window.PPCajaOBolsa;
    var $ = function (sel) { return raiz.querySelector(sel); };
    var form = $('form');
    var out = $('[data-calc-resultado]');

    function el(tag, clase, texto) {
      var e = document.createElement(tag);
      if (clase) e.className = clase;
      if (texto !== undefined) e.textContent = texto;
      return e;
    }

    function correr() {
      var f = new FormData(form);
      var r = C.decidir({
        fragilidad: f.get('fragilidad'),
        peso: f.get('peso'),
        multiPieza: f.get('multi'),
        envio: f.get('envio')
      });
      pintar(r);
    }

    // Todo el resultado se construye con createElement/textContent; los href
    // salen EXCLUSIVAMENTE de la lista fija COMPRAS — nada de innerHTML con
    // strings dinámicos.
    function pintar(r) {
      if (!out) return;
      out.hidden = false;
      out.textContent = '';

      if (!r) {
        out.appendChild(el('p', 'pp-calc__error', 'Contesta las cuatro preguntas para ver tu empaque.'));
        return;
      }

      var veredicto = el('div', 'pp-calc__veredicto pp-calc__veredicto--ok');
      veredicto.appendChild(el('p', 'pp-calc__veredicto-titulo',
        r.veredicto === 'bolsa' ? 'Tu empaque: BOLSA DE ENVÍO' : 'Tu empaque: CAJA + relleno'));
      r.razones.forEach(function (clave) {
        if (C.RAZONES[clave]) veredicto.appendChild(el('p', 'pp-calc__veredicto-dato', C.RAZONES[clave]));
      });
      if (r.dobleEmpaque) veredicto.appendChild(el('p', 'pp-calc__veredicto-dato', C.NOTAS.dobleEmpaque));
      if (r.dobleCorrugado) veredicto.appendChild(el('p', 'pp-calc__veredicto-dato', C.NOTAS.dobleCorrugado));
      if (r.sobreCaminoMedio) veredicto.appendChild(el('p', 'pp-calc__veredicto-dato', C.NOTAS.sobreCaminoMedio));
      out.appendChild(veredicto);

      var dl = el('dl', 'pp-calc__detalle');
      r.compras.forEach(function (clave) {
        var compra = C.COMPRAS[clave];
        if (!compra) return;
        var fila = el('div', 'pp-calc__detalle-fila');

        var dt = document.createElement('dt');
        var liga = document.createElement('a');
        liga.href = compra.url;
        liga.textContent = compra.titulo;
        dt.appendChild(liga);
        fila.appendChild(dt);

        fila.appendChild(el('dd', null, compra.nota));
        dl.appendChild(fila);
      });
      out.appendChild(dl);

      out.appendChild(el('p', 'pp-calc__nota',
        'Es la regla práctica de Para Paquetes por experiencia de almacén, no una norma: si tu producto es un caso raro, pregúntale a Paco aquí abajo.'));
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      correr();
      if (out) out.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    var timerRecalc = null;
    function recalcEnVivo() {
      if (!out || out.hidden) return;
      clearTimeout(timerRecalc);
      timerRecalc = setTimeout(function () { correr(); }, 350);
    }
    form.addEventListener('input', recalcEnVivo);
    form.addEventListener('change', recalcEnVivo);
  });
})();
